import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const THEME_MODES = {
	LIGHT: 'light',
	DARK: 'dark',
	SYSTEM: 'system',
};

const STORAGE_KEY = 'theme';

const ThemeContext = createContext();

export const getSystemTheme = () =>
	window.matchMedia('(prefers-color-scheme: dark)').matches ? THEME_MODES.DARK : THEME_MODES.LIGHT;

export const getStoredTheme = () => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return Object.values(THEME_MODES).includes(stored) ? stored : THEME_MODES.SYSTEM;
	} catch {
		return THEME_MODES.SYSTEM;
	}
};

const resolveTheme = (theme) => (theme === THEME_MODES.SYSTEM ? getSystemTheme() : theme);

export const applyTheme = (resolvedTheme) => {
	document.documentElement.setAttribute('data-theme', resolvedTheme);
	// Цвет верхней панели браузера на мобильных устройствах
	document
		.querySelector('meta[name="theme-color"]')
		?.setAttribute('content', resolvedTheme === THEME_MODES.DARK ? '#141312' : '#FFFFFF');
};

/**
 * Провайдер темы оформления.
 * @param {('light'|'dark')} forcedTheme — принудительная тема (например, для будущих детских
 * аккаунтов). Если задана, выбор пользователя игнорируется.
 */
export const ThemeProvider = ({ children, forcedTheme }) => {
	const [theme, setTheme] = useState(getStoredTheme);

	const resolvedTheme = forcedTheme || resolveTheme(theme);

	useEffect(() => {
		applyTheme(resolvedTheme);
	}, [resolvedTheme]);

	// Реакция на смену системной темы, когда выбран режим «Системная»
	useEffect(() => {
		const media = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = () => {
			if (!forcedTheme && getStoredTheme() === THEME_MODES.SYSTEM) {
				applyTheme(getSystemTheme());
			}
		};
		media.addEventListener('change', handleChange);
		return () => media.removeEventListener('change', handleChange);
	}, [forcedTheme]);

	const setThemeMode = useCallback(
		(mode) => {
			if (forcedTheme) return;
			try {
				localStorage.setItem(STORAGE_KEY, mode);
			} catch {
				// localStorage недоступен — тема применится только на текущую сессию
			}
			setTheme(mode);
		},
		[forcedTheme]
	);

	const value = useMemo(
		() => ({ theme, resolvedTheme, setTheme: setThemeMode, isForced: !!forcedTheme }),
		[theme, resolvedTheme, setThemeMode, forcedTheme]
	);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
